import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE_URL } from '@/constants';
import { NbButton, NbCard, NbField, NbPageHeader } from '@/components/Neobrut';

interface SignupFormData {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  confirm_password: string;
  phone_number: string;
  address: string;
  accept_terms: boolean;
}

export const Signup: FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<SignupFormData>();
  const [loading, setLoading] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const navigate = useNavigate();

  const onSubmit = async (data: SignupFormData) => {
    setLoading(true);
    setSignupError(null);
    try {
      const formattedData = { ...data, email: data.email.toLowerCase() };
      await axios.post(`${API_BASE_URL}/users/signup/`, formattedData);
      setLoading(false);
      toast.success(
        'Registro exitoso. Chequeá tu email para confirmar tu cuenta. Revisá también la carpeta de spam.',
      );
      navigate('/login');
    } catch (error) {
      console.error('Error signing up:', error);
      setLoading(false);
      setSignupError('Registro fallido. Por favor chequeá los datos e intentá de nuevo.');
    }
  };

  const password = watch('password');

  return (
    <div className="mx-auto w-full max-w-md pb-10">
      <NbPageHeader
        slug="/ signup"
        title={
          <>
            Crear
            <br />
            <span className="nb-highlight">cuenta.</span>
          </>
        }
        tone="plain"
      />

      <form onSubmit={handleSubmit(onSubmit)} className="px-4 pt-4">
        <NbCard className="flex flex-col gap-4 p-4">
          <NbField
            id="email"
            label="Email"
            type="email"
            autoComplete="email"
            error={errors.email?.message}
            {...register('email', {
              required: 'Email es requerido',
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                message: 'Email no es válido',
              },
            })}
          />
          <div className="grid grid-cols-2 gap-3">
            <NbField
              id="first_name"
              label="Nombre"
              type="text"
              autoComplete="given-name"
              error={errors.first_name?.message}
              {...register('first_name', { required: 'Nombre es requerido' })}
            />
            <NbField
              id="last_name"
              label="Apellido"
              type="text"
              autoComplete="family-name"
              error={errors.last_name?.message}
              {...register('last_name', { required: 'Apellido es requerido' })}
            />
          </div>
          <NbField
            id="password"
            label="Contraseña"
            type="password"
            autoComplete="new-password"
            hint="Mín. 8 caracteres, 1 especial, 1 número"
            error={errors.password?.message}
            {...register('password', {
              required: 'Contraseña es requerida',
              minLength: { value: 8, message: 'Mín. 8 caracteres' },
              pattern: {
                value: /^(?=.*[!@#$%^&*(),.?":{}|<>])(?=.*\d).+$/,
                message: 'Falta un carácter especial o un número',
              },
            })}
          />
          <NbField
            id="confirm_password"
            label="Confirmar contraseña"
            type="password"
            autoComplete="new-password"
            error={errors.confirm_password?.message}
            {...register('confirm_password', {
              required: 'Confirmar contraseña es requerido',
              validate: (value) => value === password || 'Las contraseñas no coinciden',
            })}
          />
          <NbField
            id="phone_number"
            label="Teléfono"
            type="tel"
            autoComplete="tel"
            hint="Con código de país (ej: +54…)"
            error={errors.phone_number?.message}
            {...register('phone_number', {
              required: 'Número de teléfono es requerido',
              pattern: {
                value: /^\+?[1-9]\d{1,14}$/,
                message: 'Número no es válido',
              },
            })}
          />
          <NbField
            id="address"
            label="Dirección (opcional)"
            type="text"
            autoComplete="street-address"
            error={errors.address?.message}
            {...register('address')}
          />

          <label
            htmlFor="accept_terms"
            className="flex cursor-pointer items-start gap-2 text-[12px] font-semibold"
          >
            <input
              type="checkbox"
              id="accept_terms"
              className="mt-0.5 h-4 w-4 shrink-0 accent-black"
              {...register('accept_terms', {
                required: 'Debés aceptar los términos y condiciones',
              })}
            />
            <span>
              Acepto los{' '}
              <button
                type="button"
                onClick={() => navigate('/tyc')}
                className="font-bold underline decoration-2 underline-offset-2"
              >
                términos y condiciones
              </button>{' '}
              y la{' '}
              <button
                type="button"
                onClick={() => navigate('/privacy-policy')}
                className="font-bold underline decoration-2 underline-offset-2"
              >
                política de privacidad
              </button>
              .
            </span>
          </label>
          {errors.accept_terms && (
            <span
              className="text-[11px] font-semibold"
              style={{ color: '#b42020', fontFamily: '"JetBrains Mono", monospace' }}
            >
              {errors.accept_terms.message}
            </span>
          )}

          {signupError && (
            <div
              className="text-[12px] font-semibold"
              style={{ color: '#b42020', fontFamily: '"JetBrains Mono", monospace' }}
            >
              {signupError}
            </div>
          )}

          <NbButton fill="ink" type="submit" className="w-full py-3" disabled={loading}>
            {loading ? 'Cargando…' : 'Registrarme'}
          </NbButton>
        </NbCard>

        <div className="mt-4 text-center text-[13px] font-medium">
          ¿Ya tenés cuenta?{' '}
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="font-bold underline decoration-2 underline-offset-2"
          >
            Iniciar sesión
          </button>
        </div>
      </form>
    </div>
  );
};
